import Analytics from '@opencare/analytics';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Authenticate } from 'connect/saga/handlers/user/authenticate';
import { selectDataUserAuthenticated } from 'connect/selectors';
import { StepFormWrapper, StepHeader, StepHeaderSubtext, StepHeaderText, StepWrapper } from 'pages/checkout/components/common/layout-styled-components';
import { CheckoutStateHandler } from 'pages/checkout/connect/state-handlers';

import { useAnalyticsService } from 'services/AnalyticsService';

import { ExperimentName, EXPERIMENTS } from 'utils/constants/experiment-configs';
import { useEffectOnce } from 'utils/customHooks/useEffectOnce';
import { useExperiment } from 'utils/customHooks/useExperiment';

import { AppointmentDetails } from './components/AppointmentDetails/AppointmentDetails';
import { AuthenticationForm } from './components/AuthenticationForm/AuthenticationForm';
import { OtpForm } from './components/AuthenticationForm/OtpForm';
import { LoggedIn } from './components/LoggedIn/LoggedIn';
import { SecurityBadgesBadge } from './components/SecurityBadges/SecurityBadgesBadge';

const analytics = new Analytics();

enum FormState {
  AUTHENTICATION,
  OTP_VERIFICATION,
  LOGGED_IN,
}

export const Account = () => {
  const [formState, setFormState] = useState<FormState>(FormState.AUTHENTICATION);
  const analyticsService = useAnalyticsService();
  const { isVariant: isSecurityMessagingVariant } = useExperiment({
    experiment: EXPERIMENTS[ExperimentName.CHECKOUT_SECURITY_MESSAGING],
  });

  // AuthenticationForm will call this saga handler, so we can watch it here to determine if we need to show the OTP form
  const authenticateResponse = useSelector(Authenticate.value!.selector);

  const otpSent = useMemo(
    () => authenticateResponse?.otpSent || false,
    [authenticateResponse],
  );

  const isUserAuthenticated = useSelector(selectDataUserAuthenticated);
  const checkoutState = useSelector(CheckoutStateHandler.selector);
  const { email } = checkoutState?.account || {};

  useEffectOnce(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-info',
      name: 'checkout',
      section: 'account-info',
      ...analyticsService.analyticsData,
    });
  });

  useEffect(() => {
    // Push setting state action to end of event loop so the forms can handle auth success
    const timeout = setTimeout(() => {
      const newFormState = (() => {
        if (isUserAuthenticated) {
          return FormState.LOGGED_IN;
        }

        if (otpSent && email) {
          return FormState.OTP_VERIFICATION;
        }

        return FormState.AUTHENTICATION;
      })();

      setFormState(newFormState);
    });

    return () => clearTimeout(timeout);
  }, [isUserAuthenticated, otpSent, email]);

  const authForm = useMemo(
    () => ({
      [FormState.AUTHENTICATION]: <AuthenticationForm />,
      [FormState.OTP_VERIFICATION]: <OtpForm />,
      [FormState.LOGGED_IN]: <LoggedIn />,
    }[formState]),
    [formState],
  );

  return (
    <StepWrapper>
      <StepFormWrapper>
        <StepHeader>
          <StepHeaderText>Book your appointment</StepHeaderText>
          <StepHeaderSubtext>
            {isSecurityMessagingVariant
              ? 'Free booking on Opencare. No cancellation fees.'
              : 'We will keep your information safe and secure.'}
          </StepHeaderSubtext>
          <SecurityBadgesBadge />
        </StepHeader>

        <AppointmentDetails />

        {authForm}
      </StepFormWrapper>
    </StepWrapper>
  );
};
